<template>
    <div>
        <Head>
            <title>{{ __('user.settings') }}: {{ user.name }}</title>
        </Head>

        <Toolbar>
            <BackButton v-if="$page.props.previous_url"/>

            <template v-slot:right>
                <ToolbarLink :href="$route('user.requests.index')">
                    <i class="fas fa-envelope-open-text"/>
                    {{ __('user.my_requests') }}
                </ToolbarLink>
                <HelpButton route="user.edit"/>
            </template>
        </Toolbar>

        <div class="d-flex flex-row gap-3">
            <div style="min-width: 250px;">
                <NavigationList :title="__('user.settings')">
                    <NavigationListItem v-for="tab in visibleTabs"
                                        :key="tab.hash"
                                        :icon="tab.icon"
                                        :name="tab.name"
                                        @select="changeTab(tab.hash)"
                                        :active="activeTab === tab.hash"
                    />
                </NavigationList>
            </div>
            <div class="flex-grow-1">
                <UserAgencySettings v-if="canAssignUserToAgency"
                                    v-show="activeTab === 'agency'"
                                    key="agency"
                                    :user="user"
                                    :agencies="agencies"
                />
                <UserBaseSettings v-show="activeTab === ''"
                                  key="default"
                                  :user="user"
                                  :registered-using-email="canChangeEmailOrPassword"
                />
                <UserSecurityAndPassword v-if="canChangeEmailOrPassword"
                                         v-show="activeTab === 'security'"
                                         key="security"
                                         :user="user"
                                         :email-changeable="canChangeEmailOrPassword"
                                         :password-changeable="canChangeEmailOrPassword"
                                         :mfa-is-enabled="mfaIsEnabled"
                />
                <UserAccountSettings v-show="activeTab === 'account'"
                                     key="account"
                                     :user="user"
                />
            </div>
        </div>
    </div>
</template>

<script>
import {defineAsyncComponent} from 'vue';
import Card from "@components/Cards/Card.vue";
import Toolbar from "@components/Toolbar.vue";
import HelpButton from "@components/HelpButton.vue";
import UserBaseSettings from "@pages/Users/Tabs/UserBaseSettings.vue";
import NavigationList from "@components/Navigation/List/NavigationList.vue";
import NavigationListItem from "@components/Navigation/List/NavigationListItem.vue";
import BackButton from "@components/BackButton.vue";
import UserSecurityAndPassword from "@pages/Users/Tabs/UserSecurityAndPassword.vue";
import UserAccountSettings from "@pages/Users/Tabs/UserAccountSettings.vue";
import ToolbarLink from "@components/ToolbarLink.vue";
import {PermissionsMixin} from "@js/permissions";
import {Head} from "@inertiajs/vue2";

export default {
    components: {
        ToolbarLink,
        UserAccountSettings,
        UserSecurityAndPassword,
        BackButton,
        NavigationListItem,
        NavigationList,
        UserBaseSettings,
        HelpButton,
        Toolbar,
        Card,
        Head,
        UserAgencySettings: defineAsyncComponent(() => import("@pages/Users/Tabs/UserAgencySettings.vue"))
    },

    mixins: [PermissionsMixin],

    props: {
        user: Object,
        canChangeEmailOrPassword: Boolean,
        mfaIsEnabled: Boolean,
        agencies: Array
    },

    data() {
        return {
            activeTab: window.location.hash.length > 0 ? window.location.hash.substring(1) : '',
            tabs: [
                {
                    icon: 'fas fa-user-cog',
                    name: __('navigation.user_settings'),
                    hash: '',
                    visible: true
                },
                {
                    icon: 'fas fa-user-lock',
                    name: __('navigation.password_and_security'),
                    hash: 'security',
                    visible: this.canChangeEmailOrPassword
                },
                {
                    icon: 'fas fa-cogs',
                    name: __('navigation.account_management'),
                    hash: 'account',
                    visible: true
                },
            ]
        }
    },

    mounted() {
        this.addAgencyTabIfUserHasAccess();
    },

    methods: {
        changeTab(targetTab) {
            window.location.hash = targetTab;
            this.activeTab = targetTab;
        },

        addAgencyTabIfUserHasAccess() {
            if (this.canAssignUserToAgency) {
                this.tabs.push({
                    icon: 'fas fa-user-secret',
                    name: __('agency.agency'),
                    hash: 'agency',
                    visible: true
                });
            }
        }
    },

    computed: {
        canAssignUserToAgency() {
            return this.Permission.ASSIGN_USER_TO_AGENCY;
        },

        visibleTabs() {
            return this.tabs.filter(tab => tab.visible)
        }
    }
}
</script>
